import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

/* Кастомный видеоплеер поверх <video> (прямая ссылка с Я.Облака / mp4).
   Бренд-«стекло», перемотка на N сек (по умолчанию 10), двойной тап по краям —
   перемотка, горячие клавиши, скорость, громкость, PiP, фуллскрин.
   timecodes: [{ t: секунды, title }] — засечки на ленте + список «Содержание».
   Через ref доступны getTime() и seekTo(sec) (для админ-редактора таймкодов). */

const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  s = Math.floor(s);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return (h > 0 ? `${h}:` : '') + `${mm}:${String(sec).padStart(2, '0')}`;
};

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const Ic = (d, sw = 2) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{d}</svg>;

function VideoPlayer({ src, poster, skip = 10, timecodes = [] }, ref) {
  const wrapRef = useRef(null);
  const [barW, setBarW] = useState('');       // вид панели по ширине плеера, а не окна
  const vidRef = useRef(null);
  const barRef = useRef(null);
  const hideTimer = useRef(null);
  const rafRef = useRef(0);
  const clickTimer = useRef(null);
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches).current;

  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [buf, setBuf] = useState(0);
  const [vol, setVol] = useState(1);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [rateOpen, setRateOpen] = useState(false);
  const [chapOpen, setChapOpen] = useState(false); // меню таймкодов в панели (в т.ч. в фуллскрине)
  const [full, setFull] = useState(false);
  const [show, setShow] = useState(true);
  const [hoverX, setHoverX] = useState(null);
  // Панель «Содержание» рядом с видео должна быть ровно его высоты.
  // Через CSS не выразить: высота плеера считается от его ширины (16:9),
  // поэтому меряем и подставляем. Ниже 900px панель уезжает под видео.
  const [sideH, setSideH] = useState(null);
  const [scrub, setScrub] = useState(null);   // доля при перетаскивании (плавно)
  const [flash, setFlash] = useState(null);   // {dir, key} — вспышка перемотки

  // ── управление ───────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = vidRef.current; if (!v) return;
    if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
  }, []);

  const seekBy = useCallback((d) => {
    const v = vidRef.current; if (!v) return;
    v.currentTime = Math.min(Math.max(0, v.currentTime + d), v.duration || 0);
  }, []);

  const seekTo = useCallback((frac) => {
    const v = vidRef.current; if (!v || !v.duration) return;
    v.currentTime = Math.min(Math.max(0, frac), 1) * v.duration;
  }, []);

  const changeVol = useCallback((dv) => {
    const v = vidRef.current; if (!v) return;
    v.muted = false; v.volume = Math.min(1, Math.max(0, (v.muted ? 0 : v.volume) + dv));
  }, []);

  const toggleMute = useCallback(() => { const v = vidRef.current; if (v) v.muted = !v.muted; }, []);

  /* Панель управления подстраивается под ширину САМОГО ПЛЕЕРА, а не окна: в уроке
     он занимает колонку и на широком экране всё равно бывает узким — тогда кнопки
     (первым делом фуллскрин) не помещались и обрезались за краем. */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      setBarW(w < 400 ? 'xs' : w < 560 ? 'sm' : '');
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toggleFull = useCallback(() => {
    const el = wrapRef.current; if (!el) return;
    const v = vidRef.current;
    // iOS Safari не умеет fullscreen для div — только для самого <video>.
    if (!document.fullscreenEnabled && v && v.webkitEnterFullscreen) {
      try { v.webkitEnterFullscreen(); return; } catch { /* ignore */ }
    }
    if (document.fullscreenElement) document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen();
    else if (v && v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  }, []);

  const togglePiP = useCallback(async () => {
    const v = vidRef.current; if (!v) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await v.requestPictureInPicture?.();
    } catch { /* */ }
  }, []);

  const flashSeek = (dir) => { setFlash({ dir, key: Date.now() }); setTimeout(() => setFlash(null), 700); };

  // ── события <video> ────────────────────────────────────────────────────────
  useEffect(() => {
    const v = vidRef.current; if (!v) return;
    const onTime = () => setCur(v.currentTime);
    const onDur = () => setDur(v.duration || 0);
    const onProg = () => { try { if (v.buffered.length) setBuf(v.buffered.end(v.buffered.length - 1)); } catch { /* */ } };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVol = () => { setVol(v.muted ? 0 : v.volume); setMuted(v.muted || v.volume === 0); };
    const onRate = () => setRate(v.playbackRate);
    v.addEventListener('timeupdate', onTime); v.addEventListener('durationchange', onDur);
    v.addEventListener('progress', onProg); v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause); v.addEventListener('volumechange', onVol);
    v.addEventListener('ratechange', onRate);
    return () => {
      v.removeEventListener('timeupdate', onTime); v.removeEventListener('durationchange', onDur);
      v.removeEventListener('progress', onProg); v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause); v.removeEventListener('volumechange', onVol);
      v.removeEventListener('ratechange', onRate);
    };
  }, [src]);

  useEffect(() => {
    const onFs = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // меню скорости — закрываем по клику вне (а не по уходу мыши)
  useEffect(() => {
    if (!rateOpen) return;
    const onDown = (e) => { if (!e.target.closest('.vp-rate:not(.vp-chapm)')) setRateOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [rateOpen]);

  // меню таймкодов — тоже закрываем по клику вне
  useEffect(() => {
    if (!chapOpen) return;
    const onDown = (e) => { if (!e.target.closest('.vp-chapm')) setChapOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [chapOpen]);

  const poke = useCallback(() => {
    setShow(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { if (!vidRef.current?.paused) setShow(false); }, 2600);
  }, []);
  useEffect(() => () => { clearTimeout(hideTimer.current); cancelAnimationFrame(rafRef.current); }, []);

  // ── горячие клавиши ────────────────────────────────────────────────────────
  const onKey = useCallback((e) => {
    if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
    switch (e.key) {
      case ' ': case 'k': case 'K': e.preventDefault(); togglePlay(); break;
      case 'ArrowRight': e.preventDefault(); seekBy(5); break;
      case 'ArrowLeft': e.preventDefault(); seekBy(-5); break;
      case 'l': case 'L': seekBy(skip); flashSeek('fwd'); break;
      case 'j': case 'J': seekBy(-skip); flashSeek('back'); break;
      case 'ArrowUp': e.preventDefault(); changeVol(0.1); break;
      case 'ArrowDown': e.preventDefault(); changeVol(-0.1); break;
      case 'm': case 'M': toggleMute(); break;
      case 'f': case 'F': toggleFull(); break;
      default: return;
    }
    poke();
  }, [togglePlay, seekBy, changeVol, toggleMute, toggleFull, poke, skip]);

  // ── перетаскивание по прогрессу (плавно, через rAF) ────────────────────────
  const fracFromX = (clientX) => {
    const r = barRef.current.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };
  const onBarDown = (e) => {
    e.preventDefault();
    const apply = (cx) => {
      const f = fracFromX(cx);
      setScrub(f);
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => seekTo(f));
    };
    apply(e.clientX);
    const move = (ev) => apply(ev.clientX);
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setScrub(null);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  // ── одиночный / двойной тап по видео ──────────────────────────────────────
  // Десктоп: клик ставит паузу. Мобилка: одиночный тап показывает/прячет
  // управление (как на YouTube), а пауза — по центральной кнопке.
  const onVideoClick = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    if (clickTimer.current) {                 // двойной тап — перемотка
      clearTimeout(clickTimer.current); clickTimer.current = null;
      if (x < r.width * 0.4) { seekBy(-skip); flashSeek('back'); }
      else if (x > r.width * 0.6) { seekBy(skip); flashSeek('fwd'); }
      else toggleFull();
      poke();
    } else {
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        if (isTouch) { if (show) setShow(false); else poke(); }
        else { togglePlay(); poke(); }
      }, 200);
    }
  };

  // Доступ снаружи (админ-редактор таймкодов)
  useImperativeHandle(ref, () => ({
    getTime: () => vidRef.current?.currentTime || 0,
    seekTo: (s) => { const v = vidRef.current; if (v) v.currentTime = Math.max(0, s); },
  }), []);

  // Таймкоды: сортировка, активная глава, переход
  const tcSorted = (timecodes || []).filter(x => x && typeof x.t === 'number').slice().sort((a, b) => a.t - b.t);
  const activeTc = (() => { let idx = -1; for (let i = 0; i < tcSorted.length; i++) { if (cur + 0.4 >= tcSorted[i].t) idx = i; else break; } return idx; })();

  // Держим панель таймкодов вровень с плеером; на узком экране (панель уходит
  // вниз) высоту не навязываем — там она по содержимому.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || tcSorted.length === 0) { setSideH(null); return; }
    const measure = () => {
      const narrow = window.matchMedia('(max-width: 900px)').matches;
      setSideH(narrow ? null : Math.round(el.getBoundingClientRect().height));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, [tcSorted.length]);

  const chapterAt = (sec) => { let title = null; for (const c of tcSorted) { if (sec + 0.0001 >= c.t) title = c.title; else break; } return title; };
  const seekToSec = (s) => { const v = vidRef.current; if (v) { v.currentTime = Math.max(0, s); v.play?.(); } };

  const playedPct = (scrub != null ? scrub : (dur ? cur / dur : 0)) * 100;
  const bufPct = dur ? (buf / dur) * 100 : 0;

  return (
    <div className={`vp-shell${tcSorted.length > 0 ? ' vp-side' : ''}`}>
    <div ref={wrapRef} className={`vp${show || !playing ? ' vp-show' : ''}${barW ? ' vp-w-sm' : ''}${barW === 'xs' ? ' vp-w-xs' : ''}`} tabIndex={0}
         onKeyDown={onKey} onMouseMove={poke} onMouseLeave={() => { if (!vidRef.current?.paused) setShow(false); }}>
      <style>{`
        .vp-shell { width: 100%; }
        /* Если есть таймкоды — видео слева, «Содержание» справа */
        .vp-shell.vp-side { display: flex; gap: 18px; align-items: flex-start; }
        .vp-side .vp { flex: 1; min-width: 0; }
        .vp-side .vp-chapters { margin-top: 0; width: 340px; flex-shrink: 0; overflow-y: auto;
          scrollbar-width: none; -ms-overflow-style: none; }
        .vp-side .vp-chapters::-webkit-scrollbar { width: 0; height: 0; }
        .vp-side .vp-chap-list { grid-template-columns: 1fr; }
        @media (max-width: 900px) {
          .vp-shell.vp-side { flex-direction: column; }
          .vp-side .vp-chapters { width: 100%; margin-top: 18px; height: auto !important; }
          .vp-side .vp-chap-list { grid-template-columns: 1fr; }
        }
        .vp { position: relative; width: 100%; aspect-ratio: 16 / 9; background: #000; outline: none; user-select: none; border-radius: 16px; overflow: hidden; }
        .vp:fullscreen { aspect-ratio: auto; height: 100%; border-radius: 0; }
        .vp video { width: 100%; height: 100%; display: block; object-fit: contain; background: #000; }
        .vp-mark { position: absolute; top: 0; width: 3px; height: 100%; background: rgba(255,255,255,0.9); transform: translateX(-50%); border-radius: 2px; pointer-events: none; }
        .vp-chapters { margin-top: 18px; background: var(--card, #fff); border: 1px solid var(--border, #e2e8f0); border-radius: 20px; padding: 20px 22px; box-shadow: 0 10px 30px -22px rgba(40,20,80,0.45); }
        .vp-ch-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .vp-ch-ic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: var(--primary-light, rgba(124,77,255,0.12)); color: var(--primary, #7c4dff); }
        .vp-ch-h1 { font-size: 14px; font-weight: 700; letter-spacing: -0.2px; color: var(--text-1, #0f172a); }
        .vp-ch-h2 { font-size: 11.5px; color: var(--text-3, #94a3b8); margin-top: 1px; }
        .vp-ch-h2 b { color: var(--primary, #7c4dff); font-weight: 700; }
        .vp-ch-dur { font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; font-weight: 600; color: var(--text-3, #94a3b8); background: var(--bg, #faf9fc); border: 1px solid var(--border, #e2e8f0); padding: 4px 10px; border-radius: 20px; }
        .vp-chap-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
        /* На узком экране главы — одной колонкой: в две они не помещались
           и список начинал ездить вбок. */
        @media (max-width: 700px) { .vp-chap-list { grid-template-columns: 1fr !important; } }
        @media (max-width: 640px) { .vp-chap-list { grid-template-columns: 1fr; } }
        .vp-chap { display: flex; align-items: center; gap: 13px; width: 100%; text-align: left; border: 1px solid var(--border, #e2e8f0); background: transparent; border-radius: 13px; padding: 11px 13px; cursor: pointer; font-family: inherit; transition: border-color .15s, background .15s; }
        .vp-chap:hover { border-color: rgba(124,77,255,0.4); }
        .vp-chap.on { background: var(--primary-light, rgba(124,77,255,0.12)); border-color: rgba(124,77,255,0.35); }
        .vp-chap-chip { position: relative; width: 38px; height: 30px; flex-shrink: 0; border-radius: 9px; display: flex; align-items: center; justify-content: center; background: var(--primary-light, #f1eefb); color: var(--text-3, #b3a8d6); }
        .vp-chap.on .vp-chap-chip { background: linear-gradient(160deg,#8b5cf6,#6d28d9); color: #fff; }
        .vp-chap-title { font-size: 14.5px; font-weight: 500; color: var(--text-1, #0f172a); letter-spacing: -0.2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .vp-chap.on .vp-chap-title { font-weight: 700; }
        .vp-chap-t { font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; font-weight: 600; color: var(--text-2, #5a5470); margin-top: 3px; }
        .vp-chap.on .vp-chap-t { color: var(--primary, #7c4dff); }
        .vp-eq { display: flex; align-items: flex-end; gap: 2px; height: 15px; }
        .vp-eq span { width: 3px; height: 8px; border-radius: 2px; background: #fff; }
        @keyframes vpeq1 { 0%,100%{height:5px} 50%{height:14px} }
        @keyframes vpeq2 { 0%,100%{height:12px} 50%{height:6px} }
        @keyframes vpeq3 { 0%,100%{height:8px} 50%{height:15px} }
        .vp-hit { position: absolute; inset: 0; cursor: pointer; }
        /* Центрирование держит обёртка, а не сама кнопка. Раньше кнопка
           центрировалась своим transform: translate(-50%,-50%), и глобальный
           press-эффект (button:active → transform: scale(.97)) его затирал —
           при настоящем нажатии кнопка прыгала вправо-вниз, курсор оказывался
           мимо неё, mouseup уходил в пустоту и click не срабатывал. */
        .vp-bigwrap { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); line-height: 0; }
        .vp-big { width: 74px; height: 74px; border-radius: 50%; border: none; display: flex; align-items: center; justify-content: center;
                  background: rgba(124,77,255,0.88); color: #fff; box-shadow: 0 10px 40px rgba(124,77,255,0.5); cursor: pointer; transition: transform 0.12s, background 0.15s; }
        .vp-big:hover { transform: scale(1.06); background: rgba(124,77,255,1); }
        .vp-big svg { width: 32px; height: 32px; }
        .vp-big svg polygon { margin-left: 3px; }
        /* Перемотка двойным тапом — как в YouTube: светлый полукруг у края, на нём
           три острых треугольника, которые загораются по очереди, и подпись. */
        .vp-flash { position: absolute; top: -25%; bottom: -25%; width: 46%; display: flex; flex-direction: column;
                    align-items: center; justify-content: center; gap: 9px; background: rgba(255,255,255,0.13);
                    color: #fff; font-size: 12.5px; font-weight: 600; letter-spacing: .2px; pointer-events: none;
                    animation: vpf 0.7s ease forwards; }
        .vp-flash.fwd { right: -8%; border-radius: 50% 0 0 50%; padding-right: 8%; }
        .vp-flash.back { left: -8%; border-radius: 0 50% 50% 0; padding-left: 8%; }
        .vp-flash-arr { display: flex; gap: 1px; }
        .vp-flash.back .vp-flash-arr { transform: scaleX(-1); }
        .vp-flash-arr i { width: 0; height: 0; border-top: 9px solid transparent; border-bottom: 9px solid transparent;
                          border-left: 13px solid #fff; opacity: .28; animation: vpArr 0.7s ease forwards; }
        .vp-flash-arr i:nth-child(2) { animation-delay: .1s; }
        .vp-flash-arr i:nth-child(3) { animation-delay: .2s; }
        @keyframes vpf { 0% { opacity: 0; } 12% { opacity: 1; } 78% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes vpArr { 0% { opacity: .28; } 30% { opacity: 1; } 65% { opacity: .28; } 100% { opacity: .28; } }
        .vp-bar { position: absolute; left: 0; right: 0; bottom: 0; padding: 30px 14px 12px;
                  background: linear-gradient(to top, rgba(8,5,16,0.9), rgba(8,5,16,0)); opacity: 0; transition: opacity 0.25s ease;
                  pointer-events: none; }
        .vp.vp-show .vp-bar { opacity: 1; pointer-events: auto; }
        .vp.vp-show .vp-hit { cursor: default; }
        .vp:not(.vp-show) { cursor: none; }
        .vp-prog { position: relative; height: 18px; display: flex; align-items: center; cursor: pointer; margin: 0 4px 6px; }
        .vp-track { position: relative; width: 100%; height: 5px; border-radius: 5px; background: rgba(255,255,255,0.22); overflow: hidden; transition: height 0.12s; }
        .vp-prog:hover .vp-track { height: 7px; }
        .vp-buf { position: absolute; inset: 0 auto 0 0; height: 100%; background: rgba(255,255,255,0.30); }
        .vp-played { position: absolute; inset: 0 auto 0 0; height: 100%; background: linear-gradient(90deg, #8b5cf6, #7c4dff); }
        .vp-knob { position: absolute; top: 50%; width: 13px; height: 13px; border-radius: 50%; background: #fff; transform: translate(-50%, -50%);
                   box-shadow: 0 1px 6px rgba(0,0,0,0.5); opacity: 0; transition: opacity 0.12s; }
        .vp-prog:hover .vp-knob { opacity: 1; }
        .vp-tip { position: absolute; bottom: 20px; transform: translateX(-50%); background: rgba(8,5,16,0.92); color: #fff;
                  font-size: 11.5px; padding: 2px 7px; border-radius: 6px; pointer-events: none; white-space: nowrap; }
        .vp-row { display: flex; align-items: center; gap: 4px; padding: 0 2px; }
        .vp-btn { width: 38px; height: 38px; border: none; background: none; color: #fff; display: inline-flex; align-items: center; justify-content: center;
                  cursor: pointer; border-radius: 9px; transition: background 0.15s; flex-shrink: 0; }
        .vp-btn:hover { background: rgba(255,255,255,0.14); }
        .vp-btn svg { width: 21px; height: 21px; }
        .vp-skip { position: relative; }
        .vp-skip svg { width: 23px; height: 23px; }
        .vp-skip span { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding-top: 1px;
                        font-size: 8px; font-weight: 800; letter-spacing: -0.4px; }
        .vp-time { font-size: 12.5px; color: #e7e2f5; font-variant-numeric: tabular-nums; margin: 0 8px; white-space: nowrap; }
        .vp-time b { color: #fff; font-weight: 600; }
        .vp-spacer { flex: 1; }
        .vp-vol { display: flex; align-items: center; gap: 2px; }
        .vp-vol input { width: 70px; height: 4px; accent-color: #7c4dff; cursor: pointer; }
        .vp-rate { position: relative; }
        .vp-rate-btn { min-width: 46px; height: 34px; padding: 0 8px; border: none; background: rgba(255,255,255,0.12); color: #fff; border-radius: 9px;
                       font-size: 12.5px; font-weight: 700; cursor: pointer; transition: background 0.15s; }
        .vp-rate-btn:hover { background: rgba(255,255,255,0.22); }
        .vp-rate-menu { position: absolute; bottom: 40px; right: 0; background: rgba(20,16,30,0.96); backdrop-filter: blur(14px);
                        border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; padding: 5px; display: flex; flex-direction: column; gap: 1px;
                        min-width: 96px; box-shadow: 0 12px 40px rgba(0,0,0,0.5); animation: vpm 0.13s ease; }
        @keyframes vpm { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .vp-rate-menu button { background: none; border: none; color: #d9d2ee; font-size: 13px; text-align: left; padding: 8px 12px; border-radius: 7px; cursor: pointer; }
        .vp-rate-menu button:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .vp-rate-menu button.on { color: #a78bfa; font-weight: 700; }

        /* Мобильная адаптация: панель не переносится на вторую строку, поэтому
           при нехватке места убираем из неё второстепенное — иначе фуллскрин
           и «картинка в картинке» просто обрезались за краем плеера.
           Перемотка ±N сек остаётся доступной двойным тапом по краям видео. */
.vp-w-sm .vp-bar { padding: 22px 6px 9px; }
        .vp-w-sm .vp-row { gap: 1px; }
        .vp-w-sm .vp-btn { width: 34px; height: 34px; }
        .vp-w-sm .vp-btn svg { width: 19px; height: 19px; }
        .vp-w-sm .vp-vol input { display: none; }
        .vp-w-sm .vp-skip { display: none; }
        .vp-w-sm .vp-pip { display: none; }
        .vp-w-sm .vp-time { font-size: 11px; margin: 0 4px; }
        .vp-w-sm .vp-rate-btn { min-width: 38px; height: 30px; font-size: 11px; padding: 0 5px; }
        .vp-w-sm .vp-big { width: 60px; height: 60px; }
        .vp-w-sm .vp-big svg { width: 26px; height: 26px; }
        /* Совсем узкий плеер — убираем ещё и скорость (меню таймкодов оставляем) */
        .vp-w-xs .vp-rate:not(.vp-chapm) { display: none; }
        /* Страховка: панель не выезжает за край ни при какой ширине */
        .vp-row { min-width: 0; }
        .vp-time { min-width: 0; overflow: hidden; }
        /* Порядок слоёв: центральная кнопка и панель — над зоной кликов */
        .vp-hit { z-index: 2; }
        .vp-bigwrap { z-index: 4; }
        .vp-flash { z-index: 4; }
        .vp-bar { z-index: 5; }
        /* Меню таймкодов в панели (работает и в фуллскрине) */
        .vp-chap-menu { position: absolute; bottom: 40px; right: 0; background: rgba(20,16,30,0.96);
          -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 10px; padding: 5px; display: flex; flex-direction: column; gap: 1px;
          min-width: 230px; max-width: 320px; max-height: 260px; overflow-y: auto;
          box-shadow: 0 12px 40px rgba(0,0,0,0.5); animation: vpm 0.13s ease; z-index: 6; }
        .vp-chap-menu button { display: flex; align-items: baseline; gap: 10px; background: none; border: none;
          color: #d9d2ee; font-size: 13px; text-align: left; padding: 8px 11px; border-radius: 7px; cursor: pointer; width: 100%; }
        .vp-chap-menu button:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .vp-chap-menu button.on { color: #a78bfa; }
        .vp-chap-menu-t { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11.5px; font-weight: 600; color: #a99fc7; flex-shrink: 0; }
        .vp-chap-menu button.on .vp-chap-menu-t { color: #a78bfa; }
        .vp-chap-menu-title { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500; }
      `}</style>

      <video ref={vidRef} src={src} poster={poster} playsInline preload="metadata" />

      {/* Зона кликов (одиночный/двойной тап) */}
      <div className="vp-hit" onClick={onVideoClick} />

      {/* Центральная кнопка: на паузе всегда; на тач-устройстве при показанном
          управлении — чтобы можно было поставить паузу тапом по центру. */}
      {(!playing || (isTouch && show)) && (
        <div className="vp-bigwrap">
          <button type="button" className="vp-big" onClick={(e) => { e.stopPropagation(); if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; } togglePlay(); poke(); }}>
            {playing
              ? Ic(<><rect x="7" y="5" width="3.4" height="14" rx="1" fill="currentColor" stroke="none" /><rect x="13.6" y="5" width="3.4" height="14" rx="1" fill="currentColor" stroke="none" /></>)
              : Ic(<polygon points="7 4 20 12 7 20 7 4" fill="currentColor" stroke="none" />)}
          </button>
        </div>
      )}

      {/* Вспышка перемотки по двойному тапу */}
      {flash && (
        <div className={`vp-flash ${flash.dir}`} key={flash.key}>
          <span className="vp-flash-arr"><i /><i /><i /></span>
          <span>{skip} {skip % 10 === 1 && skip % 100 !== 11 ? 'секунда' : [2, 3, 4].includes(skip % 10) && ![12, 13, 14].includes(skip % 100) ? 'секунды' : 'секунд'}</span>
        </div>
      )}

      {/* Панель */}
      <div className="vp-bar">
        <div className="vp-prog" ref={barRef} onPointerDown={onBarDown}
             onMouseMove={(e) => { const r = barRef.current.getBoundingClientRect(); setHoverX(e.clientX - r.left); }}
             onMouseLeave={() => setHoverX(null)}>
          {hoverX != null && dur > 0 && (() => {
            const ht = (hoverX / barRef.current.getBoundingClientRect().width) * dur;
            const ch = chapterAt(ht);
            return <div className="vp-tip" style={{ left: hoverX }}>{ch ? `${ch} · ` : ''}{fmt(ht)}</div>;
          })()}
          <div className="vp-track">
            <div className="vp-buf" style={{ width: `${bufPct}%` }} />
            <div className="vp-played" style={{ width: `${playedPct}%` }} />
            {dur > 0 && tcSorted.map((c, i) => (
              <span key={i} className="vp-mark" style={{ left: `${(c.t / dur) * 100}%` }} />
            ))}
          </div>
          <div className="vp-knob" style={{ left: `${playedPct}%` }} />
        </div>

        <div className="vp-row">
          <button className="vp-btn" onClick={togglePlay} title={playing ? 'Пауза (k)' : 'Смотреть (k)'}>
            {playing ? Ic(<><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" /><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" /></>)
                     : Ic(<polygon points="7 4 20 12 7 20 7 4" fill="currentColor" stroke="none" />)}
          </button>
          <button className="vp-btn vp-skip" onClick={() => { seekBy(-skip); flashSeek('back'); }} title={`Назад ${skip} сек (j)`}>
            {Ic(<><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>, 2.2)}<span>{skip}</span>
          </button>
          <button className="vp-btn vp-skip" onClick={() => { seekBy(skip); flashSeek('fwd'); }} title={`Вперёд ${skip} сек (l)`}>
            {Ic(<><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>, 2.2)}<span>{skip}</span>
          </button>

          <span className="vp-time"><b>{fmt(cur)}</b> / {fmt(dur)}</span>

          <div className="vp-spacer" />

          <div className="vp-vol">
            <button className="vp-btn" onClick={toggleMute} title="Звук (m)">
              {muted || vol === 0
                ? Ic(<><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" /><line x1="22" y1="9" x2="16" y2="15" /><line x1="16" y1="9" x2="22" y2="15" /></>)
                : Ic(<><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 6a9 9 0 0 1 0 12" /></>)}
            </button>
            <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : vol}
              onChange={(e) => { const v = vidRef.current; if (v) { v.muted = false; v.volume = Number(e.target.value); } }} />
          </div>

          {tcSorted.length > 0 && (
            <div className="vp-rate vp-chapm">
              <button type="button" className="vp-btn" onClick={() => setChapOpen(o => !o)} title="Таймкоды">
                {Ic(<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>)}
              </button>
              {chapOpen && (
                <div className="vp-chap-menu">
                  {tcSorted.map((c, i) => (
                    <button type="button" key={i} className={i === activeTc ? 'on' : ''} onClick={() => { seekToSec(c.t); setChapOpen(false); }}>
                      <span className="vp-chap-menu-t">{fmt(c.t)}</span>
                      <span className="vp-chap-menu-title">{c.title || 'Без названия'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="vp-rate">
            <button className="vp-rate-btn" onClick={() => setRateOpen(o => !o)} title="Скорость">{rate}×</button>
            {rateOpen && (
              <div className="vp-rate-menu">
                {RATES.map(r => (
                  <button key={r} className={r === rate ? 'on' : ''} onClick={() => { const v = vidRef.current; if (v) v.playbackRate = r; setRateOpen(false); }}>
                    {r === 1 ? 'Обычная' : `${r}×`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {'pictureInPictureEnabled' in document && (
            <button className="vp-btn vp-pip" onClick={togglePiP} title="Картинка в картинке">
              {Ic(<><rect x="2" y="4" width="20" height="16" rx="2" /><rect x="12" y="11" width="8" height="6" rx="1" fill="currentColor" stroke="none" /></>)}
            </button>
          )}

          <button className="vp-btn" onClick={toggleFull} title="На весь экран (f)">
            {full ? Ic(<><path d="M9 3v3a2 2 0 0 1-2 2H4M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" /></>)
                  : Ic(<><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" /></>)}
          </button>
        </div>
      </div>
    </div>

      {tcSorted.length > 0 && (
        <div className="vp-chapters" style={sideH ? { height: sideH } : undefined}>
          <div className="vp-ch-head">
            <div className="vp-ch-ic">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="vp-ch-h1">Содержание</div>
              <div className="vp-ch-h2">
                {activeTc >= 0 && <>Сейчас: <b>{tcSorted[activeTc].title || 'Без названия'}</b> · </>}
                {tcSorted.length} глав
              </div>
            </div>
            {dur > 0 && <span className="vp-ch-dur">{fmt(dur)}</span>}
          </div>
          <div className="vp-chap-list">
            {tcSorted.map((c, i) => {
              const on = i === activeTc;
              return (
                <button key={i} className={`vp-chap${on ? ' on' : ''}`} onClick={() => seekToSec(c.t)}>
                  <span className="vp-chap-chip">
                    {on ? (
                      <span className="vp-eq">
                        <span style={{ animation: 'vpeq1 .9s ease-in-out infinite' }} />
                        <span style={{ animation: 'vpeq2 .9s ease-in-out infinite' }} />
                        <span style={{ animation: 'vpeq3 .9s ease-in-out infinite' }} />
                      </span>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="7 4 20 12 7 20 7 4"/></svg>
                    )}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="vp-chap-title">{c.title || 'Без названия'}</div>
                    <div className="vp-chap-t">{fmt(c.t)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(VideoPlayer);
